import * as DialogPrimitive from '@radix-ui/react-dialog'
import { JobDetailContent } from '@/components/jobs/JobDetailContent'

// Radix menus/popovers render in a portal outside Dialog.Content. Remember
// pointer events that begin while one is open so dismissing the child popper
// does not also dismiss the parent job-detail drawer.
const pointerDownWithPopperOpen = new WeakSet<Event>()
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    event => {
      if (document.querySelector('[data-radix-popper-content-wrapper]')) {
        pointerDownWithPopperOpen.add(event)
      }
    },
    true
  )
}

interface JobDetailDrawerProps {
  // Selected agenda job id; null keeps the drawer closed. The Agenda page owns
  // this state, so the calendar (week/day) underneath stays mounted and its
  // navigation state is preserved when the drawer closes.
  jobId: string | null
  initialTab?: string
  onClose: () => void
}

// Feedback #15 — job details open as a right-side drawer OVERLAID on the
// Calendar, not a separate route. Built on Radix Dialog (the project's only
// overlay primitive) anchored to the right edge with a fixed width, so the
// agenda grid behind is dimmed/covered but never shrinks or reflows.
// Closable via the header X (inside JobDetailContent), overlay click, or Esc.
export function JobDetailDrawer({
  jobId,
  initialTab,
  onClose,
}: JobDetailDrawerProps) {
  const open = jobId !== null

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={next => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          className='tf-right-drawer-fixed-short bg-background duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          aria-describedby={undefined}
          onInteractOutside={event => {
            const original = event.detail.originalEvent
            const eventTarget = original.target as Element | null
            if (
              pointerDownWithPopperOpen.has(original) ||
              eventTarget?.closest('[data-radix-popper-content-wrapper]')
            ) {
              event.preventDefault()
            }
          }}
        >
          {/* Radix requires a Title for a11y; visually hidden because
              JobDetailContent renders its own header (job reference). */}
          <DialogPrimitive.Title className='sr-only'>
            {jobId ? `Job ${jobId}` : ''}
          </DialogPrimitive.Title>
          {/* CR content owns its full-bleed spacing. Keeping drawer padding here
              would make a min-height child stop short of the bottom edge. */}
          <div className='h-full'>
            {jobId && (
              <JobDetailContent
                key={`${jobId}:${initialTab ?? 'summary'}`}
                id={jobId}
                variant='drawer'
                initialTab={initialTab}
                onClose={onClose}
              />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
